"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

import { INDUSTRY_PRESETS, type IndustryPreset, type IndustryPresetKey } from "@/data/industryPresets";
import { isIndustryPresetKey } from "@/shared/utils/resolveActivePresetKey";
import { captureUtmFromLocation } from "@/shared/utils/utm";

/** Platform = ключевое УТП главной; industry = УТП выбранного пресета. */
export type HomeScope = "platform" | "industry";

type IndustryPresetContextValue = {
    homeScope: HomeScope;
    /** Always a concrete industry (for slides/tariffs); meaningful when homeScope === "industry". */
    activePreset: IndustryPresetKey;
    preset: IndustryPreset;
    isPlatform: boolean;
    selectPlatform: () => void;
    setActivePreset: (key: IndustryPresetKey) => void;
};

const IndustryPresetContext = createContext<IndustryPresetContextValue | null>(null);

function readLocation(): { scope: HomeScope; preset: IndustryPresetKey | null } {
    if (typeof window === "undefined") return { scope: "platform", preset: null };
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view")?.toLowerCase();
    if (view === "home" || view === "platform") return { scope: "platform", preset: null };

    const preset = params.get("preset")?.toLowerCase();
    if (isIndustryPresetKey(preset)) return { scope: "industry", preset };

    const campaign = params.get("utm_campaign")?.toLowerCase() ?? "";
    if (campaign.includes("beauty_100") || campaign.includes("beauty100")) {
        return { scope: "industry", preset: "beauty" };
    }
    return { scope: "platform", preset: null };
}

function writeLocation(scope: HomeScope, key: IndustryPresetKey) {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (scope === "platform") {
        url.searchParams.delete("preset");
        url.searchParams.set("view", "home");
    } else {
        url.searchParams.delete("view");
        url.searchParams.set("preset", key);
    }
    window.history.replaceState(window.history.state, "", url.toString());
}

export function IndustryPresetProvider({ children }: { children: ReactNode }) {
    const [homeScope, setHomeScope] = useState<HomeScope>("platform");
    const [activePreset, setActivePresetState] = useState<IndustryPresetKey>("horeca");

    useEffect(() => {
        captureUtmFromLocation();
        const fromUrl = readLocation();
        setHomeScope(fromUrl.scope);
        if (fromUrl.preset) setActivePresetState(fromUrl.preset);
    }, []);

    const selectPlatform = useCallback(() => {
        setHomeScope("platform");
        writeLocation("platform", activePreset);
    }, [activePreset]);

    const setActivePreset = useCallback((key: IndustryPresetKey) => {
        setActivePresetState(key);
        setHomeScope("industry");
        writeLocation("industry", key);
    }, []);

    const value = useMemo(
        () => ({
            homeScope,
            activePreset,
            preset: INDUSTRY_PRESETS[activePreset],
            isPlatform: homeScope === "platform",
            selectPlatform,
            setActivePreset,
        }),
        [homeScope, activePreset, selectPlatform, setActivePreset],
    );

    return (
        <IndustryPresetContext.Provider value={value}>{children}</IndustryPresetContext.Provider>
    );
}

export function useIndustryPreset() {
    const ctx = useContext(IndustryPresetContext);
    if (!ctx) {
        throw new Error("useIndustryPreset must be used within IndustryPresetProvider");
    }
    return ctx;
}

/** Safe for pages without IndustryPresetProvider (e.g. solution landings). */
export function useOptionalIndustryPreset() {
    return useContext(IndustryPresetContext);
}
